import { useRef, useState } from "react";
import { api, idr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, FilePlus2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyItem = () => ({ _k: Math.random().toString(36).slice(2), product_name: "", series: "", quantity: 1, unit: "Unit", unit_price: 0 });
const emptyAddress = () => ({ street: "", city: "", state: "", zip: "", country: "Indonesia" });
const emptyForm = () => ({
  customer_name: "", customer_company: "", customer_email: "", customer_phone: "",
  customer_address: emptyAddress(), po_number: "", custom_invoice_number: "",
  subject: "", tax_percent: 11, notes: "",
});

export function DirectInvoiceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([emptyItem()]);
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddress = (k, v) => setForm((f) => ({ ...f, customer_address: { ...f.customer_address, [k]: v } }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, emptyItem()]);
  const delItem = (i) => setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const taxAmount = subtotal * Number(form.tax_percent || 0) / 100;
  const total = subtotal + taxAmount;

  const reset = () => {
    setForm(emptyForm());
    setItems([emptyItem()]);
  };

  const fillFromPo = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/extract-po", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 200000 });
      setForm((f) => ({
        ...f,
        customer_name: data.customer_name || f.customer_name,
        customer_company: data.customer_company || f.customer_company,
        customer_email: data.customer_email || f.customer_email,
        customer_phone: data.customer_phone || f.customer_phone,
        po_number: data.po_number || f.po_number,
        subject: data.subject || f.subject,
        tax_percent: data.tax_percent ?? f.tax_percent,
        customer_address: { ...emptyAddress(), ...Object.fromEntries(Object.entries(data.customer_address || {}).filter(([, v]) => v)) },
      }));
      if (Array.isArray(data.items) && data.items.length) {
        setItems(data.items.map((it) => ({
          _k: Math.random().toString(36).slice(2),
          product_name: it.product_name || "", series: it.series || "",
          quantity: Number(it.quantity || 1), unit: it.unit || "Unit", unit_price: Number(it.unit_price || 0),
        })));
      }
      toast.success(`Data PO terbaca: ${data.items?.length || 0} barang. Periksa sebelum menerbitkan.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membaca PO");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Nama customer wajib diisi");
    if (items.some((it) => !it.product_name.trim())) return toast.error("Nama barang wajib diisi di setiap baris");
    setBusy(true);
    try {
      const payload = {
        ...form,
        tax_percent: Number(form.tax_percent || 0),
        items: items.map((it) => ({
          product_name: it.product_name, series: it.series,
          quantity: Number(it.quantity || 1), unit: it.unit || "Unit",
          unit_price: Number(it.unit_price || 0),
        })),
      };
      const { data } = await api.post("/invoices/direct", payload);
      if (data.zoho_synced) {
        const zt = data.zoho_tax_mode === "zoho_tax" ? "Zoho + PPN resmi" : "Zoho";
        toast.success(`Invoice ${data.invoice_number} dibuat · ${zt} (${data.zoho_invoice_number})`);
      } else {
        toast.warning(`Invoice ${data.invoice_number} dibuat (lokal), gagal sinkron Zoho: ${data.zoho_error || "-"}. Coba 'Sinkron ke Zoho' di detail.`);
      }
      setOpen(false);
      reset();
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="open-direct-invoice-btn" onClick={() => setOpen(true)} className="rounded-sm bg-accent text-accent-foreground hover:opacity-90">
        <FilePlus2 className="w-4 h-4 mr-2" /> Buat Invoice Langsung
      </Button>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Buat Invoice Langsung</DialogTitle>
          <DialogDescription>Terbitkan invoice tanpa melalui penawaran, langsung tersinkron ke Zoho Billing.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="border border-dashed border-primary/40 bg-primary/5 rounded-sm p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="di-po-upload-section">
            <div className="text-xs text-muted-foreground">
              <b className="text-foreground">Isi otomatis dari PO</b> — unggah PDF/foto PO, AI mengisi customer, alamat, No. PO & daftar barang.
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" data-testid="di-po-file-input" onChange={(e) => fillFromPo(e.target.files?.[0])} />
            <Button type="button" variant="outline" size="sm" className="rounded-sm" disabled={extracting} onClick={() => fileRef.current?.click()} data-testid="di-po-upload-btn">
              {extracting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Membaca PO…</> : <><Sparkles className="w-4 h-4 mr-2" /> Unggah PO</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nama Customer *</Label>
              <Input data-testid="di-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="rounded-sm" /></div>
            <div><Label className="text-xs">Perusahaan</Label>
              <Input data-testid="di-customer-company" value={form.customer_company} onChange={(e) => set("customer_company", e.target.value)} className="rounded-sm" /></div>
            <div><Label className="text-xs">Email</Label>
              <Input data-testid="di-customer-email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="rounded-sm" /></div>
            <div><Label className="text-xs">Telp / WA</Label>
              <Input data-testid="di-customer-phone" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="rounded-sm" /></div>
            <div><Label className="text-xs">No. PO (Order Number)</Label>
              <Input data-testid="di-po-number" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} placeholder="mis. VTOC009841" className="rounded-sm" /></div>
            <div><Label className="text-xs">No. Invoice Kustom (opsional)</Label>
              <Input data-testid="di-custom-number" value={form.custom_invoice_number} onChange={(e) => set("custom_invoice_number", e.target.value)} placeholder="mis. INV-0826009" className="rounded-sm" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Hal / Subjek</Label>
              <Input data-testid="di-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="rounded-sm" /></div>
          </div>

          <div className="border border-border rounded-sm p-3 space-y-3" data-testid="di-address-section">
            <div>
              <Label className="text-xs">Alamat Customer (Billing & Shipping)</Label>
              <Textarea data-testid="di-address-street" value={form.customer_address.street} onChange={(e) => setAddress("street", e.target.value)} placeholder="Nama gedung, jalan, nomor, kelurahan/kecamatan" className="rounded-sm bg-secondary min-h-[70px]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">Kota</Label>
                <Input data-testid="di-address-city" value={form.customer_address.city} onChange={(e) => setAddress("city", e.target.value)} className="rounded-sm" /></div>
              <div><Label className="text-xs">Provinsi</Label>
                <Input data-testid="di-address-state" value={form.customer_address.state} onChange={(e) => setAddress("state", e.target.value)} className="rounded-sm" /></div>
              <div><Label className="text-xs">Kode Pos</Label>
                <Input data-testid="di-address-zip" value={form.customer_address.zip} onChange={(e) => setAddress("zip", e.target.value)} className="rounded-sm" /></div>
              <div><Label className="text-xs">Negara</Label>
                <Input data-testid="di-address-country" value={form.customer_address.country} onChange={(e) => setAddress("country", e.target.value)} className="rounded-sm" /></div>
            </div>
          </div>

          <div className="border border-border rounded-sm">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-secondary overline text-muted-foreground">
              <div className="col-span-5">Barang / Jasa</div>
              <div className="col-span-2 text-center">Qty</div>
              <div className="col-span-1 text-center">Sat.</div>
              <div className="col-span-3 text-right">Harga Satuan</div>
              <div className="col-span-1"></div>
            </div>
            <div className="divide-y divide-border">
              {items.map((it, i) => (
                <div key={it._k || i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`di-item-row-${i}`}>
                  <div className="col-span-5 space-y-1">
                    <Input data-testid={`di-item-name-${i}`} value={it.product_name} onChange={(e) => setItem(i, "product_name", e.target.value)} placeholder="Nama barang" className="rounded-sm h-8" />
                    <Input data-testid={`di-item-series-${i}`} value={it.series} onChange={(e) => setItem(i, "series", e.target.value)} placeholder="Series / PN (opsional)" className="rounded-sm h-7 text-xs" />
                  </div>
                  <div className="col-span-2"><Input data-testid={`di-item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="rounded-sm h-8 text-center" /></div>
                  <div className="col-span-1"><Input data-testid={`di-item-unit-${i}`} value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="rounded-sm h-8 text-center px-1" /></div>
                  <div className="col-span-3"><Input data-testid={`di-item-price-${i}`} type="number" min="0" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="rounded-sm h-8 text-right font-mono" /></div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => delItem(i)} data-testid={`di-item-del-${i}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addItem} data-testid="di-add-item" className="w-full px-3 py-2 text-xs text-primary hover:bg-secondary/50 flex items-center justify-center gap-1 border-t border-border">
              <Plus className="w-3 h-3" /> Tambah Baris
            </button>
          </div>

          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="w-32">
              <Label className="text-xs">PPN (%)</Label>
              <Input data-testid="di-tax-percent" type="number" min="0" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} className="rounded-sm" />
            </div>
            <div className="text-right space-y-1 flex-1 min-w-[200px]">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono" data-testid="di-subtotal">{idr(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>PPN ({form.tax_percent || 0}%)</span><span className="font-mono" data-testid="di-tax">{idr(taxAmount)}</span></div>
              <div className="flex justify-between font-heading font-black text-lg pt-1 border-t border-border"><span>Total</span><span className="font-mono" data-testid="di-total">{idr(total)}</span></div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy}>Batal</Button>
          <Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="di-submit-btn">
            {busy ? "Menerbitkan…" : "Terbitkan & Kirim ke Zoho"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
